import { afterNextRender, afterRenderEffect, Component, computed, effect, ElementRef, inject, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HybridService } from '@core/hybrid/hybrid.service';
import { PlatformService } from '@core/platform.service';
import { ProgressBarService } from '@core/progress-bar.service';
import { APP_ROUTES } from '@core/routes';
import { MediaQueryService } from '@core/services/media-query.service';
import { PageTrackerService } from '@core/services/page-tracker.service';
import { isAuthenticated } from '@features/auth/store/auth.signals';
import { ChatbotComponent } from '@features/chatbot/components/chatbot/chatbot.component';
import { ChatbotAccountContextService } from '@features/chatbot/services/chatbot-account-context';
import { ChatbotCustomerContextService } from '@features/chatbot/services/chatbot-customer-context';
import { ChatbotLayoutService } from '@features/chatbot/services/chatbot-layout.service';
import { ChatbotNavigationContextService } from '@features/chatbot/services/chatbot-navigation-context';
import { ChatbotPageWorkflowService } from '@features/chatbot/services/chatbot-page-workflow.service';
import { ChatbotPopupService } from '@features/chatbot/services/chatbot-popup.service';
import { ChatbotSavedSearchContextService } from '@features/chatbot/services/chatbot-saved-search-context';
import { ChatbotSearchContextService } from '@features/chatbot/services/chatbot-search-context';
import { ChatbotSearchResultsContextService } from '@features/chatbot/services/chatbot-search-results-context';
import { ChatbotSelectedListingsContextService } from '@features/chatbot/services/chatbot-selected-listings-context';
import { ChatbotSessionService } from '@features/chatbot/services/chatbot-session.service';
import { ChatbotSurfaceService } from '@features/chatbot/services/chatbot-surface.service';
import { getChatbotAutoHide, setChatbotAutoHide } from '@features/chatbot/store/chatbot-sidebar.storage';
import { FeatureFlagsStore } from '@features/feature-flags/store/feature-flags.store';
import { ToastStackComponent } from '@layout/toast-stack/components/toast-stack/toast-stack.component';
import { PnycButtonComponent } from '@ui-kit/pnyc-button/pnyc-button.component';
import { PnycCheckboxState } from '@ui-kit/pnyc-checkbox/models/pnyc-checkbox-state';
import { PnycPopupService } from '@ui-kit/popup/pnyc-popup.service';
import { ProgressBarComponent } from '@ui-kit/progress-bar/progress-bar.component';
import { filter, take } from 'rxjs';
import { NavigationPanelComponent } from './layout/navigation-panel/navigation-panel.component';
import { NavigationPanelService } from './layout/navigation-panel/services/navigation-panel.service';
import { ScrollToTopContainerDirective } from './layout/scroll-to-top/scroll-to-top-container.directive';
import { ScrollToTopComponent } from './layout/scroll-to-top/scroll-to-top.component';
import { ToolbarComponent } from './layout/toolbar/toolbar.component';
import { PublicToolbarComponent } from './layout/public-toolbar/public-toolbar.component';
import { PreviewComponent } from './layout/preview/preview.component';
import { PreviewService } from './layout/preview/preview.service';
import { VersionBadgeComponent } from './layout/version-badge/version-badge.component';

const AUTOHIDE_COLLAPSE_DELAY = 2000;
const SIDEBAR_ANIMATION_DURATION = 300;
const PUBLIC_ROUTES = [
    APP_ROUTES.SHARED_REPORT,
    APP_ROUTES.PUBLIC_BUILDING_SHARE,
    APP_ROUTES.PUBLIC_LISTING_DETAILS,
    APP_ROUTES.PUBLIC_MORTGAGE_CALCULATOR,
    APP_ROUTES.PUBLIC_SEARCH,
];

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    imports: [
        RouterOutlet,
        ProgressBarComponent,
        ScrollToTopComponent,
        ScrollToTopContainerDirective,
        PnycButtonComponent,
        NavigationPanelComponent,
        ToastStackComponent,
        ToolbarComponent,
        PublicToolbarComponent,
        VersionBadgeComponent,
        ChatbotComponent,
        PreviewComponent,
    ],
})
export class AppComponent {
    public readonly isUserAuthorized = isAuthenticated;
    public readonly isShowSmartBanner = signal<boolean>(false);
    public readonly isChatbotVisible = signal<boolean>(false);
    public readonly isSidebarOpen = signal<boolean>(false);
    public readonly isAutoHideEnabled = signal<boolean>(false);
    public readonly isSidebarCollapsed = signal<boolean>(false);
    public readonly isChatInputFocused = signal<boolean>(false);

    public readonly autoHideCheckboxState = computed<PnycCheckboxState>(() =>
        this.isAutoHideEnabled() ? 'checked' : 'unchecked'
    );

    public readonly platform = inject(PlatformService);
    public readonly hybridService = inject(HybridService);
    private readonly navigationPanelService = inject(NavigationPanelService);
    readonly isNavPanelVisible = this.navigationPanelService.isVisible;

    private readonly router = inject(Router);
    private readonly progressBar = inject(ProgressBarService);
    private readonly pageTracker = inject(PageTrackerService);
    private readonly navigationContext = inject(ChatbotNavigationContextService);
    private readonly searchContext = inject(ChatbotSearchContextService);
    private readonly pageWorkflow = inject(ChatbotPageWorkflowService);
    private readonly accountContext = inject(ChatbotAccountContextService);
    private readonly customerContext = inject(ChatbotCustomerContextService);
    private readonly savedSearchContext = inject(ChatbotSavedSearchContextService);
    private readonly selectedListingsContext = inject(ChatbotSelectedListingsContextService);
    private readonly searchResultsContext = inject(ChatbotSearchResultsContextService);
    private readonly mediaQueryService = inject(MediaQueryService);
    private readonly chatbotPopupService = inject(ChatbotPopupService);
    private readonly sessionService = inject(ChatbotSessionService);
    private readonly surfaceService = inject(ChatbotSurfaceService);
    public readonly chatbotLayout = inject(ChatbotLayoutService);
    public readonly previewService = inject(PreviewService);
    private readonly popupService = inject(PnycPopupService);
    private readonly featureFlagsStore = inject(FeatureFlagsStore);

    private collapseTimer: ReturnType<typeof setTimeout> | null = null;
    private closeTimer: ReturnType<typeof setTimeout> | null = null;

    public readonly isProgressBarVisible = this.progressBar.inProgress;
    readonly isHybrid = this.hybridService.isHybrid;
    readonly isSmallDevice = computed(() => new Set(['xs', 'sm', 'md']).has(this.mediaQueryService.activeMediaQuery()));

    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        ),
        { initialValue: null }
    );

    readonly isPublicPage = computed(() => {
        const fullUrl = this.currentUrl()?.urlAfterRedirects ?? this.router.url;
        const url = fullUrl.split(/[?#]/)[0];

        return PUBLIC_ROUTES.some(route => url.startsWith(`/${route}/`) || url === `/${route}`);
    });

    readonly hidesChatbot = computed(() => {
        this.currentUrl();

        let route = this.router.routerState.snapshot.root;

        while (route.firstChild) {
            route = route.firstChild;
        }

        return route.data['hideChatbot'] === true;
    });

    readonly isChatResizing = signal(false);
    private readonly globalBlock = viewChild.required<ElementRef<HTMLElement>>('globalBlock');
    private readonly sidebarZone = viewChild<ElementRef<HTMLElement>>('sidebarZone');
    private readonly sidebarOpenPending = signal(false);

    constructor() {
        effect(() => {
            this.chatbotLayout.sizeMode();

            untracked(() => this.isChatResizing.set(true));
        });

        afterRenderEffect({
            earlyRead: () => {
                getComputedStyle(this.globalBlock().nativeElement).getPropertyValue('grid-template-columns');

                return this.chatbotLayout.sizeMode();
            },
            write: sizeMode => {
                sizeMode();
                this.isChatResizing.set(false);
            },
        });

        afterRenderEffect({
            earlyRead: () => {
                const zone = this.sidebarZone();

                if (!zone || !this.sidebarOpenPending()) {
                    return false;
                }

                getComputedStyle(zone.nativeElement).getPropertyValue('transform');

                return true;
            },
            write: ready => {
                if (ready()) {
                    this.sidebarOpenPending.set(false);
                    this.isSidebarOpen.set(true);
                }
            },
        });

        afterNextRender(() => {
            this.navigationContext.initialize();
            this.searchContext.initialize();
            this.pageWorkflow.initialize();
            this.isAutoHideEnabled.set(getChatbotAutoHide());
        });

        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            take(1),
        ).subscribe(() => {
            requestAnimationFrame(() => {
                this.hybridService.sendMessage({ kind: 'main-view-loaded' });
            });
        });

        this.accountContext.initialize();
        this.customerContext.initialize();
        this.savedSearchContext.initialize();
        this.selectedListingsContext.initialize();
        this.searchResultsContext.initialize();
        this.pageTracker.initialize();
        this.featureFlagsStore.initialize();

        // Close desktop sidebar when viewport shrinks below breakpoint
        effect(() => {
            const small = this.isSmallDevice();
            const visible = this.isChatbotVisible();

            if (small && visible) {
                this.closeSidebar();
            }
        });

        // The chat panel and its hub session belong to the signed-in user, so a sign-out
        // must tear both down instead of leaving them for whoever logs in next.
        effect(() => {
            const authorized = this.isUserAuthorized();

            if (!authorized) {
                untracked(() => this.closeChatbotForSignedOutUser());
            }
        });

        // React to desktop sidebar requests from any caller (search-toolbar, etc.)
        effect(() => {
            const requested = this.chatbotPopupService.desktopSidebarRequested();
            const visible = untracked(() => this.isChatbotVisible());

            if (requested && !visible) {
                untracked(() => this.openSidebar());
            } else if (!requested && visible) {
                untracked(() => this.closeSidebar());
            }
        });
    }

    public onChatbotToggled(): void {
        if (this.isSmallDevice()) {
            this.surfaceService.requestSurface('sidebar');
            this.chatbotPopupService.openPanel(null, 'bottom');

            return;
        }

        if (this.isChatbotVisible()) {
            this.chatbotPopupService.closeDesktopSidebar();
        } else {
            this.chatbotPopupService.openPanel(null, 'bottom-right');
        }
    }

    public onAutoHideChanged(enabled: boolean): void {
        this.isAutoHideEnabled.set(enabled);
        setChatbotAutoHide(enabled);

        if (!enabled && this.isSidebarCollapsed()) {
            this.isSidebarCollapsed.set(false);
        }
    }

    public onSidebarMouseEnter(): void {
        this.cancelCollapseTimer();

        if (this.isSidebarCollapsed()) {
            this.isSidebarCollapsed.set(false);
        }
    }

    public onSidebarMouseLeave(): void {
        if (!this.isAutoHideEnabled() || this.isChatInputFocused()) {
            return;
        }

        this.scheduleCollapse();
    }

    public onChatInputFocusChanged(focused: boolean): void {
        this.isChatInputFocused.set(focused);

        if (!focused && this.isAutoHideEnabled()) {
            this.scheduleCollapse();
        }
    }

    public onSidebarClosed(): void {
        this.chatbotPopupService.closeDesktopSidebar();
        this.closeSidebar();
    }

    private openSidebar(): void {
        this.cancelCloseTimer();
        void this.sessionService.connect();
        this.isChatbotVisible.set(true);
        this.isSidebarCollapsed.set(false);
        this.sidebarOpenPending.set(true);
    }

    private closeChatbotForSignedOutUser(): void {
        this.chatbotPopupService.closeChat();
        this.closeSidebar();
        this.sessionService.cancelActiveWidget();
        this.sessionService.disconnect();
        this.sessionService.resetChat();
        this.surfaceService.releaseSurface('inline');
    }

    private closeSidebar(): void {
        this.cancelCollapseTimer();
        this.cancelCloseTimer();
        this.sidebarOpenPending.set(false);
        this.isSidebarOpen.set(false);
        this.isSidebarCollapsed.set(false);
        this.closeTimer = setTimeout(() => {
            this.isChatbotVisible.set(false);
            this.surfaceService.releaseSurface('sidebar');

            if (this.chatbotLayout.isListingSelection()) {
                this.chatbotLayout.resetToLastSize();
            }
        }, SIDEBAR_ANIMATION_DURATION);
    }

    private cancelCloseTimer(): void {
        if (this.closeTimer !== null) {
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
        }
    }

    private scheduleCollapse(): void {
        this.cancelCollapseTimer();
        this.collapseTimer = setTimeout(() => {
            if (this.isAutoHideEnabled() && this.isChatbotVisible() && !this.isChatInputFocused()) {
                this.isSidebarCollapsed.set(true);
            }
        }, AUTOHIDE_COLLAPSE_DELAY);
    }

    private cancelCollapseTimer(): void {
        if (this.collapseTimer !== null) {
            clearTimeout(this.collapseTimer);
            this.collapseTimer = null;
        }
    }
}
